import { useFormikContext } from "formik";
import React, { useEffect, useMemo } from "react";

import useToast from "@/contexts/toast-context";

import ProductForm from "@/components/templates/ProductForm";
import { ProductFormPageMessages } from "@/messages/products/product-form-page.messages";
import useCreateProductWithSkus from "@/hooks/requests/product/use-create-product-sku";
import useUpdateProductWithSkus from "@/hooks/requests/product/use-update-product-sku";
import IProduct from "@/interfaces/product.interface";

import { ProductFormData } from "./types";

type Props = {
  messages: ProductFormPageMessages;
  isEditing: boolean;
  product: IProduct | null;
  customerId: string | null;
};

export const FormWrapper: React.FC<Props> = ({ messages, isEditing, product, customerId }) => {
  const formik = useFormikContext<ProductFormData>();
  const { showToast } = useToast();
  
  // Construir produto temporário se foi criado automaticamente mas não está disponível como prop
  const currentProduct = useMemo(() => {
    if (product) return product;
    
    // Se o produto tem ID mas não está disponível como prop, construir um objeto temporário
    const productId = formik.values.id;
    if (productId) {
      // Remover campos que não fazem parte de IProduct
      const {
        selectedCategories,
        isEditing,
        isEditingSku,
        sku,
        skusToDelete,
        skus,
        variations,
        ...productData
      } = formik.values;
      
      // Converter tipos para IProduct (usando type assertion pois são estruturas similares)
      // O useUpdateProductWithSkus aceita produto temporário apenas para obter ID e código
      const productSkus = (skus || []) as unknown as IProduct["skus"];
      const productVariations = (variations || []) as unknown as IProduct["variations"];
      
      return {
        ...productData,
        id: productId,
        skus: productSkus,
        variations: productVariations,
        rowDate: new Date(), // IProduct requer rowDate
      } as unknown as IProduct;
    }
    
    return null;
  }, [product, formik.values]);

  const useUpdate = useUpdateProductWithSkus({ messages, product: currentProduct, customerId });
  const useCreate = useCreateProductWithSkus({ messages, customerId });
  const { isLoading: isLoadingUpdate, isLoadingUpdateImg } = useUpdate;
  const { isLoading: isLoadingCreate, isLoadingCreateImg } = useCreate;
  const isSaving = React.useRef(false);

  const isLoading = isLoadingUpdate || isLoadingCreate || isLoadingCreateImg || isLoadingUpdateImg;

  // Criar função de submit customizada
  const handleSubmit = async () => {
    // Prevenir múltiplas chamadas simultâneas
    if (isSaving.current) {
      return;
    }
    
    const errors = await formik.validateForm();

    if (Object.keys(errors).length > 0) {
      // Marca os campos como tocados para exibir os erros no formulário
      formik.setTouched(
        Object.keys(errors).reduce((acc, key) => ({ ...acc, [key]: true }), {}),
        false
      );

      const firstError = Object.values(errors).find(
        (error) => typeof error === "string"
      ) as string | undefined;

      showToast({
        type: "error",
        text: firstError ?? "Verifique os campos do formulário antes de salvar.",
        duration: 5000,
      });

      return;
    }

    {
      isSaving.current = true;
      
      try {
        // Se o produto já existe (tem ID), sempre usar PUT para evitar duplicação
        // Isso acontece quando o produto foi criado automaticamente ao clicar em "criar SKU"
        const productId = formik.values.id || product?.id;
        const productExists = !!productId;

        if (isEditing || productExists) {
          // Se está editando OU o produto já foi criado, usar PUT
          await useUpdate.handleUpdate(formik.values, true); // true = mostrar mensagem de sucesso
        } else {
          // Apenas criar novo produto se realmente não existir
          await useCreate.handleCreate(formik.values, true); // true = mostrar mensagem de sucesso
        }
      } finally {
        isSaving.current = false;
      }
    }
  };

  return (
    <ProductForm
      messages={messages}
      isLoading={isLoading}
      onSubmit={handleSubmit}
      productId={product?.id ?? null}
      isEditing={isEditing}
    />
  );
};
